import type { FeedPost } from '@wlb/shared/types/post';
import { resolveImageUrl } from '../../utils/resolveImageUrl';

/**
 * `FeedPost.image` is deliberately broad (a React Native `ImageSource`, a
 * `{ uri }` object, a bare path or an array of any of those) because the same
 * type feeds the mobile app. Flattening it here keeps every admin surface from
 * re-implementing the union check, and every entry goes through
 * `resolveImageUrl` so relative API paths become absolute.
 */
const flatten = (media: FeedPost['image'] | FeedPost['images']): string[] => {
  if (media == null) return [];
  if (Array.isArray(media)) return media.flatMap(item => flatten(item));
  const url = resolveImageUrl(media);
  return url ? [url] : [];
};

/** Every image on a post, primary first, de-duplicated. */
export const resolvePostImages = (
  post: Pick<FeedPost, 'image' | 'images'>,
): readonly string[] => {
  const album = flatten(post.images);
  const images = album.length > 0 ? album : flatten(post.image);
  return [...new Set(images)];
};

/** The post's video URL, or null when the post carries no video. */
export const resolvePostVideo = (
  post: Pick<FeedPost, 'video' | 'videoUrl'>,
): string | null => {
  const uri = post.video?.uri ?? post.videoUrl ?? null;
  return uri && uri.trim() ? uri : null;
};

/**
 * Blog bodies arrive as stored HTML. The admin panel only ever reads a post, so
 * the markup is reduced to its text rather than rendered: an operator scanning
 * a queue must not be shown raw tags, and nothing here should be able to inject
 * markup into the panel.
 */
export const postPlainText = (value: string | null | undefined): string => {
  if (!value) return '';
  return value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};
